import { X, ArrowUpRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function ResourcesSidebar({
  isOpen,
  onClose,
  resources,
  isDarkMode,
}) {
  const safeResources = Array.isArray(resources) ? resources : [];

  const totalSources = safeResources.reduce(
    (sum, item) =>
      sum + (Array.isArray(item?.sources) ? item.sources.length : 0),
    0
  );

  const exportCSV = () => {
    if (!safeResources.length) return;

    const allSources = safeResources.flatMap((r) =>
      Array.isArray(r?.sources) ? r.sources : []
    );

    if (!allSources.length) return;

    const csvRows = [
      ['Title', 'Domain', 'URL', 'Snippet'],
      ...allSources.map((src) => [
        `"${src?.title ?? ''}"`,
        `"${src?.domain ?? ''}"`,
        `"${src?.url ?? ''}"`,
        `"${(src?.snippet ?? '').replace(/"/g, '""')}"`,
      ]),
    ];

    const csvString = csvRows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'sources.csv';
    a.click();

    URL.revokeObjectURL(url);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.15 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-0 bg-black/10 backdrop-blur-sm z-40"
            onClick={() => onClose?.()}
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            style={{ contain: 'layout paint size' }}
            className={`fixed right-0 top-0 h-full w-80 z-50 border-l shadow-xl flex flex-col
              ${
                isDarkMode
                  ? 'bg-gray-900 border-gray-700'
                  : 'bg-white border-gray-200'
              }`}
          >
            {/* Header */}
            <div
              className={`flex items-center justify-between p-4 border-b shrink-0
                ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
            >
              <h2 className="text-sm font-semibold">
                Sources {totalSources > 0 && `(${totalSources})`}
              </h2>
              <button
                onClick={() => onClose?.()}
                className="p-1 rounded transition hover:bg-brand hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div
              className="flex-1 overflow-y-auto super-ultra-thin p-4 space-y-5"
              style={{
                WebkitOverflowScrolling: 'touch',
                willChange: 'scroll-position',
              }}
            >
              {safeResources.length === 0 && (
                <p className="text-xs opacity-60">
                  No research sources available.
                </p>
              )}

              {safeResources.map((item, idx) => {
                if (!item || !Array.isArray(item.sources)) return null;

                return (
                  <div key={idx} className="space-y-3">
                    <div
                      className={`text-xs font-bold tracking-wide uppercase
                        ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      Query: {item.query ?? 'Unknown'}
                    </div>

                    {item.sources.map((src, i) => {
                      if (!src || !src.url) return null;

                      return (
                        <a
                          key={i}
                          href={src.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`block p-3 rounded border transition-colors
                            ${
                              isDarkMode
                                ? 'border-gray-700 bg-gray-800 hover:bg-gray-700'
                                : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                            }`}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-brand truncate">
                                {src.title ?? 'Untitled Source'}
                              </div>
                              <div className="text-xs opacity-70 truncate">
                                {src.domain ?? ''}
                              </div>
                            </div>

                            <ArrowUpRight
                              size={16}
                              className="opacity-70 shrink-0"
                            />
                          </div>

                          {src.snippet && (
                            <p className="text-xs opacity-60 line-clamp-2 mt-1">
                              {src.snippet}
                            </p>
                          )}
                        </a>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div
              className={`p-3 border-t shrink-0
                ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
            >
              <button
                onClick={exportCSV}
                disabled={totalSources === 0}
                className={`w-full text-xs font-medium py-2 rounded-lg border transition
                  ${
                    isDarkMode
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-800 disabled:opacity-50'
                      : 'border-indigo-300 text-indigo-500 hover:bg-indigo-50 disabled:opacity-50'
                  }`}
              >
                Export Sources
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
